import { icon, propertyTypeIcon } from '../../icons';
import { i18nService, t, translateDocumentType, translatePropertyStatus, translatePropertyType } from '../../services/i18n';
import type { DocumentDto, PropertyDto, PropertyListItemDto, PropertyUnitSummaryDto, ReminderDto, RentalAgreementDto } from '../../services/api';
import { renderRichText } from '../../services/rich-text';

export type PropertyViewMode = 'cards' | 'list';
export type DetailTab = 'tenants' | 'rent-history' | 'occupancy' | 'renewals' | 'turnovers' | 'documents' | 'gallery' | 'reminders' | 'maintenance' | 'radar' | 'insurances' | 'damage-reports' | 'public-page';
export type RadarItemKind = 'document' | 'agreement' | 'reminder';
export type RadarUrgency = 'overdue' | 'soon' | 'upcoming';

export interface RadarItem {
  id: string;
  kind: RadarItemKind;
  title: string;
  meta: string;
  dueDate: string;
  timestamp: number;
  urgency: RadarUrgency;
  tab: DetailTab;
}

export const PROPERTY_STATUS_OPTIONS = ['Rented', 'Vacant', 'UnderRenovation', 'ForSale'] as const;
export const PROPERTY_DOC_TYPES = ['Epc', 'Inspection', 'Insurance', 'Invoice', 'Photo', 'Other'];
export const PROPERTY_MAINTENANCE_CATEGORIES = ['Maintenance', 'Repair', 'Inspection', 'Renovation', 'Other'] as const;

export const PROPERTY_ICONS = {
  plus: icon('plus', { size: 14, strokeWidth: 2.5 }),
  plusSm: icon('plus', { size: 10, strokeWidth: 3 }),
  close: icon('x', { size: 20 }),
  copy: icon('copy', { size: 16, attrs: 'stroke-linecap="round" stroke-linejoin="round"' }),
  edit: icon('edit', { size: 15, attrs: 'stroke-linecap="round" stroke-linejoin="round"' }),
  trend: icon('trend-up', { size: 15, attrs: 'stroke-linecap="round" stroke-linejoin="round"' }),
  dossier: icon('file', { size: 14, strokeWidth: 2.2 }),
  grid: icon('grid', { size: 16 }),
  list: icon('list', { size: 16 }),
  import: icon('file-text', { size: 16, strokeWidth: 2.2 }),
  home: icon('home', { size: 48, strokeWidth: 1.5, attrs: 'opacity="0.4"' }),
};

export function escapeHtml(str: string): string {
  const el = document.createElement('span');
  el.textContent = str;
  return el.innerHTML;
}

export function getPropertyLabel(property: Pick<PropertyDto, 'displayName' | 'address'> | Pick<PropertyListItemDto, 'displayName' | 'address'>): string {
  return property.displayName || property.address;
}

export function getEditorValue(root: ParentNode, name: string): string | undefined {
  const editor = root.querySelector(`rc-rich-text-editor[name="${name}"]`) as HTMLElement & { value?: string } | null;
  const value = editor?.value?.trim() || '';
  return value || undefined;
}

export function formatDate(dateStr?: string): string {
  if (!dateStr) return '-';
  return i18nService.formatDate(dateStr, { day: 'numeric', month: 'short', year: 'numeric' });
}

export function getPropertyStatusBadgeClass(status: string): string {
  if (status === 'Rented') return 'badge-success';
  if (status === 'Vacant') return 'badge-warning';
  if (status === 'UnderRenovation') return 'badge-info';
  return 'badge-muted';
}

export function isAgreementActive(agreement: RentalAgreementDto): boolean {
  if (!agreement.endDate) return true;
  return new Date(agreement.endDate) >= new Date();
}

export function renderPropertyCard(property: PropertyListItemDto): string {
  const units = property.units || [];
  const reminderBadge = property.overdueReminderCount > 0
    ? `<span class="badge badge-danger" style="font-size:0.65rem;padding:0.15rem 0.4rem;">${property.overdueReminderCount} ${t('common.overdue')}</span>`
    : property.openReminderCount > 0
      ? `<span class="badge badge-warning" style="font-size:0.65rem;padding:0.15rem 0.4rem;">${property.openReminderCount} ${t('common.open')}</span>`
      : '';

  return `
    <div class="card property-card">
      <div class="property-card-header">
        <button class="property-card-title-area" data-action="open-details" data-id="${property.id}">
          <div class="property-icon">${propertyTypeIcon(property.type, { size: 24, strokeWidth: 2.1 })}</div>
          <div class="property-card-meta">
            <div class="card-title">${escapeHtml(getPropertyLabel(property))}</div>
            <div style="display:flex;align-items:center;gap:0.4rem;flex-wrap:wrap;margin-top:0.2rem;">
              <span class="badge badge-info">${escapeHtml(translatePropertyType(property.type))}</span>
              <span class="badge ${getPropertyStatusBadgeClass(property.status)}">${escapeHtml(translatePropertyStatus(property.status))}</span>
              ${reminderBadge}
              ${property.structureType === 'Building' ? `<span class="badge badge-muted">${units.length} ${t(units.length === 1 ? 'property.unitSingular' : 'property.unitPlural')}</span>` : ''}
            </div>
          </div>
        </button>
      </div>
      ${property.description ? `<div class="card-desc">${escapeHtml(property.description)}</div>` : ''}
      ${property.activeRentAmount != null ? `<div class="property-rent">${i18nService.formatCurrency(property.activeRentAmount)}<span class="property-rent-unit">${t('property.perMonth')}</span></div>` : ''}
      <div class="property-stats">
        <div class="property-stat-group">
          <button class="property-stat-link" data-action="open-tab" data-tab="tenants" data-id="${property.id}">
            ${icon('user', { size: 13 })}
            <span>${property.tenantCount} ${t(property.tenantCount === 1 ? 'property.tenantSingular' : 'property.tenantPlural')}</span>
          </button>
        </div>
        <div class="property-stat-group">
          <button class="property-stat-link" data-action="open-tab" data-tab="documents" data-id="${property.id}">
            ${icon('file', { size: 13 })}
            <span>${property.documentCount} ${t(property.documentCount === 1 ? 'property.documentSingular' : 'property.documentPlural')}</span>
          </button>
          <button class="property-stat-add" data-action="quick-add" data-auto="add-doc" data-tab="documents" data-id="${property.id}" title="${t('document.upload')}">${PROPERTY_ICONS.plusSm}</button>
        </div>
        <div class="property-stat-group">
          <button class="property-stat-link" data-action="open-tab" data-tab="reminders" data-id="${property.id}">
            ${icon('bell', { size: 13 })}
            <span>${property.openReminderCount > 0 ? `${property.openReminderCount} ${t('common.open')}` : t('common.reminders')}</span>
          </button>
          <button class="property-stat-add" data-action="quick-add" data-auto="add-reminder" data-tab="reminders" data-id="${property.id}" title="${t('reminder.add')}">${PROPERTY_ICONS.plusSm}</button>
        </div>
      </div>
      ${property.structureType === 'Building' && units.length > 0 ? renderUnitPreview(units) : ''}
      <div class="card-actions">
        <button class="btn btn-outline btn-sm" data-action="open-details" data-id="${property.id}">${t('common.details')}</button>
        <button class="btn btn-outline btn-sm" data-action="edit" data-id="${property.id}">${t('common.edit')}</button>
        <button class="btn btn-danger btn-sm" data-action="delete" data-id="${property.id}">${t('common.delete')}</button>
      </div>
    </div>
  `;
}

export function renderPropertyTable(properties: PropertyListItemDto[]): string {
  return `
    <div class="table-responsive">
      <table class="property-list-table">
        <thead>
          <tr>
            <th>${t('common.address')}</th>
            <th>${t('common.type')}</th>
            <th>${t('common.status')}</th>
            <th>${t('property.rent')}</th>
            <th>${t('nav.tenants')}</th>
            <th>${t('common.documents')}</th>
            <th>${t('common.reminders')}</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          ${properties.map((property) => {
            const reminderCell = property.overdueReminderCount > 0
              ? `<span class="badge badge-danger">${property.overdueReminderCount} ${t('common.overdue')}</span>`
              : property.openReminderCount > 0
                ? `<span class="badge badge-warning">${property.openReminderCount} ${t('common.open')}</span>`
                : `<span style="color:var(--color-text-muted);font-size:0.85rem;">-</span>`;
            const units = property.units || [];
            return `
              <tr>
                <td>
                  <button class="property-list-address" data-action="open-details" data-id="${property.id}">
                    ${propertyTypeIcon(property.type, { size: 14, strokeWidth: 2.1 })} ${escapeHtml(getPropertyLabel(property))}
                  </button>
                </td>
                <td><span class="badge badge-info">${escapeHtml(translatePropertyType(property.type))}</span></td>
                <td><span class="badge ${getPropertyStatusBadgeClass(property.status)}">${escapeHtml(translatePropertyStatus(property.status))}</span></td>
                <td>${property.activeRentAmount != null ? `${i18nService.formatCurrency(property.activeRentAmount)}${t('property.perMonth')}` : `<span style="color:var(--color-text-muted);">-</span>`}</td>
                <td><div class="property-stat-group"><button class="property-stat-link" data-action="open-tab" data-tab="tenants" data-id="${property.id}">${property.structureType === 'Building' ? `${property.occupiedUnitCount ?? 0}/${units.length} ${t(units.length === 1 ? 'property.unitSingular' : 'property.unitPlural')}` : `${property.tenantCount} ${t(property.tenantCount === 1 ? 'property.tenantSingular' : 'property.tenantPlural')}`}</button></div></td>
                <td>
                  <div class="property-stat-group">
                    <button class="property-stat-link" data-action="open-tab" data-tab="documents" data-id="${property.id}">${property.documentCount} ${t(property.documentCount === 1 ? 'property.documentSingular' : 'property.documentPlural')}</button>
                    <button class="property-stat-add" data-action="quick-add" data-auto="add-doc" data-tab="documents" data-id="${property.id}" title="${t('document.upload')}">${PROPERTY_ICONS.plusSm}</button>
                  </div>
                </td>
                <td>
                  <div class="property-stat-group">
                    <button class="property-stat-link" data-action="open-tab" data-tab="reminders" data-id="${property.id}">${reminderCell}</button>
                    <button class="property-stat-add" data-action="quick-add" data-auto="add-reminder" data-tab="reminders" data-id="${property.id}" title="${t('reminder.add')}">${PROPERTY_ICONS.plusSm}</button>
                  </div>
                </td>
                <td>
                  <div class="card-actions">
                    <button class="btn btn-outline btn-sm" data-action="open-details" data-id="${property.id}">${t('common.details')}</button>
                    <button class="btn btn-outline btn-sm" data-action="edit" data-id="${property.id}">${t('common.edit')}</button>
                    <button class="btn btn-danger btn-sm" data-action="delete" data-id="${property.id}">${t('common.delete')}</button>
                  </div>
                </td>
              </tr>
            `;
          }).join('')}
        </tbody>
      </table>
    </div>
  `;
}

export function getStartOfToday(): Date {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
}

export function getRadarWindowEnd(): Date {
  const end = getStartOfToday();
  end.setMonth(end.getMonth() + 12);
  return end;
}

export function getUrgency(timestamp: number): RadarUrgency {
  const start = getStartOfToday().getTime();
  const daysUntil = Math.ceil((timestamp - start) / 86400000);
  if (daysUntil < 0) return 'overdue';
  if (daysUntil <= 30) return 'soon';
  return 'upcoming';
}

export function getDaysUntil(dateStr: string): number {
  const timestamp = new Date(dateStr).getTime();
  return Math.ceil((timestamp - getStartOfToday().getTime()) / 86400000);
}

export function describeDueDate(dateStr: string): string {
  const daysUntil = getDaysUntil(dateStr);
  if (daysUntil === 0) return t('common.today');
  if (daysUntil < 0) return t('common.daysAgo', { count: Math.abs(daysUntil) });
  return t('common.daysRemaining', { count: daysUntil });
}

export function getUrgencyLabel(urgency: RadarUrgency): string {
  if (urgency === 'overdue') return t('common.overdue');
  if (urgency === 'soon') return t('common.soon');
  return t('property.radar.upcoming');
}

export function getUrgencyClass(urgency: RadarUrgency): string {
  if (urgency === 'overdue') return 'badge-danger';
  if (urgency === 'soon') return 'badge-warning';
  return 'badge-info';
}

export function getRadarItems(documents: DocumentDto[], agreements: RentalAgreementDto[], reminders: ReminderDto[]): RadarItem[] {
  const start = getStartOfToday().getTime();
  const end = getRadarWindowEnd().getTime();

  const documentItems = documents
    .filter((document) => Boolean(document.expiryDate))
    .map((document) => ({
      id: document.id,
      kind: 'document' as const,
      title: document.fileName,
      meta: translateDocumentType(document.documentType),
      dueDate: document.expiryDate!,
      timestamp: new Date(document.expiryDate!).getTime(),
      tab: 'documents' as const,
    }));

  const agreementItems = agreements
    .filter((agreement) => Boolean(agreement.endDate) && new Date(agreement.endDate!).getTime() >= start)
    .map((agreement) => ({
      id: agreement.id,
      kind: 'agreement' as const,
      title: agreement.tenantName,
      meta: t('property.radar.contractEnds'),
      dueDate: agreement.endDate!,
      timestamp: new Date(agreement.endDate!).getTime(),
      tab: 'tenants' as const,
    }));

  const reminderItems = reminders
    .filter((reminder) => !reminder.isCompleted && !reminder.documentId)
    .map((reminder) => ({
      id: reminder.id,
      kind: 'reminder' as const,
      title: reminder.title,
      meta: t('common.reminder'),
      dueDate: reminder.dueDate,
      timestamp: new Date(reminder.dueDate).getTime(),
      tab: 'reminders' as const,
    }));

  return [...documentItems, ...agreementItems, ...reminderItems]
    .filter((item) => !Number.isNaN(item.timestamp) && (item.timestamp < start || item.timestamp <= end))
    .sort((left, right) => left.timestamp - right.timestamp)
    .map((item) => ({
      ...item,
      urgency: getUrgency(item.timestamp),
    }));
}

export function renderPropertySummary(property: PropertyDto): string {
  const units = property.units || [];
  return `
    <div class="property-summary-shell">
      <div class="property-summary-main">
        <div class="property-summary-topline">
          <div class="property-summary-address-block">
            <span class="detail-info-label">${property.structureType === 'Building' ? t('common.property') : t('common.address')}</span>
            <span class="property-summary-address-value">${escapeHtml(getPropertyLabel(property))}</span>
            ${property.structureType !== 'Standalone' ? `<span class="detail-info-label" style="margin-top:0.35rem;">${t('common.address')}</span><span class="detail-info-value">${escapeHtml(property.address)}</span>` : ''}
          </div>
          <div class="property-summary-facts">
            <div class="detail-info-row property-summary-fact-card">
              <span class="detail-info-label">${t('common.type')}</span>
              <span class="detail-info-value"><span class="badge badge-info">${escapeHtml(translatePropertyType(property.type))}</span></span>
            </div>
            <div class="detail-info-row property-summary-fact-card">
              <span class="detail-info-label">${t('common.status')}</span>
              <span class="detail-info-value"><span class="badge ${getPropertyStatusBadgeClass(property.status)}">${escapeHtml(translatePropertyStatus(property.status))}</span></span>
            </div>
          </div>
        </div>
        ${property.structureType === 'Building' && units.length > 0 ? `
          <div class="property-summary-section">
            <span class="detail-info-label">${t('property.units.title')}</span>
            <div class="property-unit-grid">
              ${units.map((unit) => renderPropertyUnitCard(unit)).join('')}
            </div>
          </div>
        ` : ''}
        ${property.description || property.notes ? `
          <div class="property-summary-rich-grid">
            ${property.description ? `
              <div class="detail-info-row property-summary-section">
                <span class="detail-info-label">${t('common.description')}</span>
                <span class="detail-info-value property-summary-copy">${escapeHtml(property.description)}</span>
              </div>
            ` : ''}
            ${property.notes ? `
              <div class="detail-info-row property-summary-section">
                <span class="detail-info-label">${t('common.notes')}</span>
                <div class="detail-info-value property-summary-copy">${renderRichText(property.notes)}</div>
              </div>
            ` : ''}
          </div>
        ` : ''}
      </div>
      <div class="property-summary-actions">
        <button class="btn btn-dossier btn-sm" data-action="export-dossier">
          ${PROPERTY_ICONS.dossier}
          ${t('dossier.buttonShort')}
        </button>
        <button class="btn btn-outline btn-sm property-summary-edit-button" data-action="edit-property">
          ${PROPERTY_ICONS.edit}
          ${t('common.edit')}
        </button>
      </div>
    </div>
  `;
}

function renderUnitPreview(units: PropertyUnitSummaryDto[]): string {
  return `
    <div class="property-unit-preview">
      ${units.slice(0, 6).map((unit) => `
        <button class="property-unit-pill" data-action="open-details" data-id="${unit.id}">
          <span>${escapeHtml(unit.displayName)}</span>
          <span class="badge ${getPropertyStatusBadgeClass(unit.status)}">${escapeHtml(translatePropertyStatus(unit.status))}</span>
        </button>
      `).join('')}
    </div>
  `;
}

function renderPropertyUnitCard(unit: PropertyUnitSummaryDto): string {
  return `
    <button class="property-unit-card" data-action="open-details" data-id="${unit.id}">
      <strong>${escapeHtml(unit.displayName)}</strong>
      <span class="badge ${getPropertyStatusBadgeClass(unit.status)}">${escapeHtml(translatePropertyStatus(unit.status))}</span>
      <span class="text-muted">${unit.tenantCount} ${t(unit.tenantCount === 1 ? 'property.tenantSingular' : 'property.tenantPlural')}</span>
    </button>
  `;
}
